<template>
  <DetailTemplate
    :customClass="'payment-detail detail-page'"
    v-if="getPermission('payment:view')"
  >
    <!-- Collapsible Sidebar Detail -->
    <template v-slot:sidebar-detail>
      <v-card class="h-100" elevation="0">
        <!-- Status Header -->
        <div class="d-flex align-center justify-space-between px-0">
          <template v-if="pageLoading">
            <v-skeleton-loader
              class="custom-skeleton height-30px my-auto mx-2"
              width="130"
              type="text"
            ></v-skeleton-loader>
          </template>
          <template v-else>
            <CustomStatus
              v-if="detail && detail.status && false"
              :status="detail.status"
              endpoint="payment/status"
            ></CustomStatus>
          </template>
        </div>

        <!-- Prominent Payment Amount -->
        <div class="mb-3">
          <template v-if="pageLoading">
            <v-skeleton-loader
              class="custom-skeleton my-auto"
              width="150"
              height="32"
              type="text"
            ></v-skeleton-loader>
          </template>
          <template v-else>
            <span class="font-weight-bold color-custom-blue font-size-22 ml-1">
              {{ formatMoney(detail.amount) }}
            </span>
          </template>
        </div>

        <!-- Skeleton for Sidebar Table -->
        <template v-if="pageLoading">
          <v-skeleton-loader
            v-for="(d, i) in 6"
            :key="i"
            class="custom-skeleton height-40px custom-skeleton-full-width mr-5"
            :class="{ 'mt-3': i <= 0, 'my-1': i > 0 }"
            type="text"
          ></v-skeleton-loader>
        </template>

        <template v-else>
          <!-- Section 1: Payment Information -->
          <div class="mt-4">
            <table width="100%">
              <tr>
                <th
                  colspan="2"
                  class="grey lighten-4 font-size-16 color-custom-blue py-1 px-3 mb-2 text-uppercase text-left"
                >
                  Payment Information
                </th>
              </tr>

              <!-- Payment # -->
              <tr>
                <td
                  valign="middle"
                  width="140"
                  class="py-1 font-size-18 font-weight-500 border-bottom-dashed"
                >
                  Payment #
                </td>
                <td
                  valign="middle"
                  class="py-1 font-size-18 border-bottom-dashed"
                >
                  <v-chip
                    label
                    color="blue darken-4"
                    outlined
                    class="text-white px-2 font-weight-600"
                    small
                  >
                    <v-icon small class="mr-1">mdi-receipt</v-icon>
                    {{ detail.barcode }}
                  </v-chip>
                </td>
              </tr>

              <!-- Reference # -->
              <tr>
                <td
                  valign="middle"
                  width="140"
                  class="py-1 font-size-18 font-weight-500 border-bottom-dashed"
                >
                  Reference #
                </td>
                <td
                  valign="middle"
                  class="py-1 font-size-18 border-bottom-dashed custom-nowrap-ellipsis"
                  v-on:dblclick="updateField('reference')"
                >
                  <template v-if="updateDetailDialog.reference">
                    <v-text-field
                      ref="reference"
                      v-model.trim="updateDetail.reference"
                      dense
                      filled
                      color="cyan"
                      label="Reference #"
                      :rules="[
                        validateRules.minLength(
                          updateDetail.reference,
                          'Reference',
                          1
                        ),
                        validateRules.maxLength(
                          updateDetail.reference,
                          'Reference',
                          100
                        ),
                      ]"
                      solo
                      flat
                      hide-details
                      :loading="particuleFieldLoading"
                      :disabled="particuleFieldLoading"
                      @keydown.esc="onEscParticularField"
                      @keydown.enter="updateParticularField"
                    ></v-text-field>
                  </template>
                  <template v-else>
                    <span
                      :class="{
                        'custom-bottom-border': canUpdatePayment,
                      }"
                    >
                      <template v-if="detail.reference">{{
                        detail.reference
                      }}</template>
                      <template v-else
                        ><em class="text--secondary font-weight-500"
                          >No Reference</em
                        ></template
                      >
                    </span>
                    <UpdateTooltip v-if="canUpdatePayment"></UpdateTooltip>
                  </template>
                </td>
              </tr>

              <!-- Payment Mode -->
              <tr>
                <td
                  valign="middle"
                  width="140"
                  class="py-1 font-size-18 font-weight-500 border-bottom-dashed"
                >
                  Payment Mode
                </td>
                <td
                  valign="middle"
                  class="py-1 font-size-18 border-bottom-dashed custom-nowrap-ellipsis"
                  v-on:dblclick="updateField('payment_mode')"
                >
                  <template v-if="updateDetailDialog.payment_mode">
                    <v-select
                      :items="paymentModeList"
                      ref="payment_mode"
                      v-model.trim="updateDetail.payment_mode"
                      dense
                      filled
                      solo
                      flat
                      hide-details
                      :loading="particuleFieldLoading"
                      :disabled="particuleFieldLoading"
                      label="Payment Mode"
                      color="cyan"
                      item-text="text"
                      item-value="id"
                      item-color="cyan"
                      @keydown.esc="onEscParticularField"
                      @change="updateParticularField"
                    ></v-select>
                  </template>
                  <template v-else>
                    <span
                      :class="{
                        'custom-bottom-border': canUpdatePayment,
                      }"
                    >
                      <template v-if="detail.payment_mode">{{
                        detail.payment_mode.text
                      }}</template>
                      <template v-else
                        ><em class="text--secondary font-weight-500"
                          >No Payment Mode</em
                        ></template
                      >
                    </span>
                    <UpdateTooltip v-if="canUpdatePayment"></UpdateTooltip>
                  </template>
                </td>
              </tr>

              <!-- Payment Date -->
              <tr>
                <td
                  valign="middle"
                  width="140"
                  class="py-1 font-size-18 font-weight-500 border-bottom-dashed"
                >
                  Payment Date
                </td>
                <td
                  valign="middle"
                  class="py-1 font-size-18 border-bottom-dashed custom-nowrap-ellipsis"
                  v-on:dblclick="updateField('payment_date')"
                >
                  <template v-if="updateDetailDialog.payment_date">
                    <v-menu
                      v-model="paymentDatePicker"
                      :close-on-content-click="false"
                      :nudge-right="40"
                      transition="scale-transition"
                      offset-y
                      min-width="290px"
                      @keydown.esc="onEscParticularField"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          ref="payment_date"
                          dense
                          filled
                          flat
                          label="Payment Date"
                          hide-details
                          solo
                          prepend-inner-icon="mdi-calendar"
                          readonly
                          v-bind="attrs"
                          v-on="on"
                          :value="getFormattedDate(updateDetail.payment_date)"
                          color="cyan"
                          :loading="particuleFieldLoading"
                          :disabled="particuleFieldLoading"
                          @keydown.esc="onEscParticularField"
                          @keydown.enter="updateParticularField"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model.trim="updateDetail.payment_date"
                        color="cyan"
                        @keydown.esc="onEscParticularField"
                        v-on:input="updateParticularField"
                      >
                        <v-spacer></v-spacer>
                        <v-btn
                          text
                          color="cyan"
                          @click="paymentDatePicker = false"
                        >
                          Cancel
                        </v-btn>
                      </v-date-picker>
                    </v-menu>
                  </template>
                  <template v-else>
                    <span
                      :class="{
                        'custom-bottom-border': canUpdatePayment,
                      }"
                    >
                      <template v-if="detail.payment_date">
                        <v-icon small color="blue darken-4" class="mr-1"
                          >mdi-calendar</v-icon
                        >
                        {{ formatDate(detail.payment_date) }}
                      </template>
                      <template v-else
                        ><em class="text--secondary font-weight-500"
                          >No Payment Date</em
                        ></template
                      >
                    </span>
                    <UpdateTooltip v-if="canUpdatePayment"></UpdateTooltip>
                  </template>
                </td>
              </tr>

              <!-- Amount Received -->
              <tr>
                <td
                  valign="middle"
                  width="140"
                  class="py-1 font-size-18 font-weight-500 border-bottom-dashed"
                >
                  Amount Received
                </td>
                <td
                  valign="middle"
                  class="py-1 font-size-18 border-bottom-dashed green--text font-weight-600"
                  v-on:dblclick="updateField('amount')"
                >
                  <template v-if="updateDetailDialog.amount">
                    <v-text-field
                      ref="amount"
                      v-model.trim="updateDetail.amount"
                      dense
                      filled
                      color="cyan"
                      label="Amount Received"
                      :rules="[
                        validateRules.minLength(
                          updateDetail.amount,
                          'Amount Received',
                          1
                        ),
                        validateRules.maxLength(
                          updateDetail.amount,
                          'Amount Received',
                          100
                        ),
                      ]"
                      solo
                      flat
                      hide-details
                      :loading="particuleFieldLoading"
                      :disabled="particuleFieldLoading"
                      @keydown.esc="onEscParticularField"
                      @keydown.enter="updateParticularField"
                    ></v-text-field>
                  </template>
                  <template v-else>
                    <span
                      :class="{
                        'custom-bottom-border': canUpdatePayment,
                      }"
                    >
                      <v-icon small color="green" class="mr-1">mdi-cash</v-icon>
                      <template v-if="detail.amount">{{
                        formatMoney(detail.amount)
                      }}</template>
                      <template v-else
                        ><em class="text--secondary font-weight-500"
                          >No Amount</em
                        ></template
                      >
                    </span>
                    <UpdateTooltip v-if="canUpdatePayment"></UpdateTooltip>
                  </template>
                </td>
              </tr>

              <!-- Transaction ID -->
              <tr>
                <td
                  valign="middle"
                  width="140"
                  class="py-1 font-size-18 font-weight-500 border-bottom-dashed"
                >
                  Transaction ID
                </td>
                <td
                  valign="middle"
                  class="py-1 font-size-18 border-bottom-dashed custom-nowrap-ellipsis"
                  v-on:dblclick="updateField('transaction_id')"
                >
                  <template v-if="updateDetailDialog.transaction_id">
                    <v-text-field
                      ref="transaction_id"
                      v-model.trim="updateDetail.transaction_id"
                      dense
                      filled
                      color="cyan"
                      label="Transaction ID"
                      :rules="[
                        validateRules.minLength(
                          updateDetail.transaction_id,
                          'Transaction ID',
                          1
                        ),
                        validateRules.maxLength(
                          updateDetail.transaction_id,
                          'Transaction ID',
                          100
                        ),
                      ]"
                      solo
                      flat
                      hide-details
                      :loading="particuleFieldLoading"
                      :disabled="particuleFieldLoading"
                      @keydown.esc="onEscParticularField"
                      @keydown.enter="updateParticularField"
                    ></v-text-field>
                  </template>
                  <template v-else>
                    <span
                      :class="{
                        'custom-bottom-border': canUpdatePayment,
                      }"
                    >
                      <template v-if="detail.transaction_id">{{
                        detail.transaction_id
                      }}</template>
                      <template v-else
                        ><em class="text--secondary font-weight-500"
                          >No Transaction ID</em
                        ></template
                      >
                    </span>
                    <UpdateTooltip v-if="canUpdatePayment"></UpdateTooltip>
                  </template>
                </td>
              </tr>
            </table>
          </div>

          <!-- Section 2: Invoice Information -->
          <div class="mt-4" v-if="detail.invoice && detail.invoice.id">
            <table width="100%">
              <tr>
                <th
                  colspan="2"
                  class="grey lighten-4 font-size-16 color-custom-blue py-1 px-3 mb-2 text-uppercase text-left"
                >
                  Invoice Information
                </th>
              </tr>
              <tr>
                <td
                  valign="middle"
                  width="140"
                  class="py-1 font-size-18 font-weight-500 border-bottom-dashed"
                >
                  Invoice #
                </td>
                <td
                  valign="middle"
                  class="py-1 font-size-18 border-bottom-dashed"
                >
                  <router-link
                    :to="
                      getDefaultRoute('invoice.detail', {
                        params: { id: detail.invoice.id },
                      })
                    "
                  >
                    <v-chip
                      label
                      color="blue darken-4"
                      outlined
                      class="text-white font-weight-600"
                      small
                    >
                      {{ detail.invoice.barcode }}
                    </v-chip>
                  </router-link>
                </td>
              </tr>
              <tr v-if="detail.invoice.invoice_date">
                <td
                  valign="middle"
                  width="140"
                  class="py-1 font-size-18 font-weight-500 border-bottom-dashed"
                >
                  Invoice Date
                </td>
                <td
                  valign="middle"
                  class="py-1 font-size-18 border-bottom-dashed"
                >
                  <v-icon small color="blue darken-4" class="mr-1"
                    >mdi-calendar</v-icon
                  >
                  {{ formatDate(detail.invoice.invoice_date) }}
                </td>
              </tr>
              <tr v-if="detail.invoice.sent_date">
                <td
                  valign="middle"
                  width="140"
                  class="py-1 font-size-18 font-weight-500 border-bottom-dashed"
                >
                  Sent Date
                </td>
                <td
                  valign="middle"
                  class="py-1 font-size-18 border-bottom-dashed blue--text"
                >
                  <v-icon small color="blue" class="mr-1"
                    >mdi-calendar-alert</v-icon
                  >
                  {{
                    lodash.isEmpty(detail.invoice) === false
                      ? formatDate(detail.invoice.sent_date)
                      : "-"
                  }}
                </td>
              </tr>
              <tr v-if="detail.invoice.due_date">
                <td
                  valign="middle"
                  width="140"
                  class="py-1 font-size-18 font-weight-500 border-bottom-dashed"
                >
                  Due Date
                </td>
                <td
                  valign="middle"
                  class="py-1 font-size-18 border-bottom-dashed red--text"
                >
                  <v-icon small color="red" class="mr-1"
                    >mdi-calendar-alert</v-icon
                  >
                  {{ formatDate(detail.invoice.due_date) }}
                </td>
              </tr>
              <tr v-if="detail.invoice.total">
                <td
                  valign="middle"
                  width="140"
                  class="py-1 font-size-18 font-weight-500 border-bottom-dashed"
                >
                  Invoice Amount
                </td>
                <td
                  valign="middle"
                  class="py-1 font-size-18 border-bottom-dashed orange--text font-weight-600"
                >
                  {{ formatMoney(detail.invoice.total) }}
                </td>
              </tr>
              <tr v-if="detail.invoice.pending_amount">
                <td
                  valign="middle"
                  width="140"
                  class="py-1 font-size-18 font-weight-500 border-bottom-dashed"
                >
                  Balance Amount
                </td>
                <td
                  valign="middle"
                  class="py-1 font-size-18 border-bottom-dashed red--text font-weight-600"
                >
                  {{ formatMoney(detail.invoice.pending_amount) }}
                </td>
              </tr>
            </table>
          </div>
        </template>
      </v-card>
    </template>

    <!-- Header Title -->
    <template v-slot:header-title>
      <div class="d-flex align-center">
        <v-skeleton-loader
          class="custom-skeleton"
          v-if="pageLoading"
          width="300"
          height="28"
          type="text"
        ></v-skeleton-loader>
        <template v-else>
          <h1 class="form-title custom-nowrap-ellipsis text-capitalize mr-2">
            {{ detail.barcode }}
          </h1>
          <CustomStatus
            v-if="detail && detail.status && false"
            :status="detail.status"
            endpoint="payment/status"
          ></CustomStatus>
        </template>
      </div>
    </template>

    <!-- Header Actions -->
    <template v-slot:header-action>
      <div class="d-flex align-center justify-end">
        <v-menu
          transition="slide-y-transition"
          bottom
          content-class="custom-menu-list"
          offset-y
          left
          v-if="moreActions && moreActions.length"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-if="false"
              depressed
              :disabled="pageLoading"
              class="mx-2 custom-bold-button white--text"
              color="cyan"
              v-bind="attrs"
              v-on="on"
            >
              More... <v-icon right>mdi-chevron-down</v-icon>
            </v-btn>
          </template>
          <v-list>
            <template v-for="(more, index) in moreActions">
              <v-list-item
                link
                v-on:click="updateMoreAction(more.action)"
                :key="index"
                :disabled="more.disabled"
              >
                <v-list-item-icon class="margin-auto-zero mr-3 my-2">
                  <v-icon class="icon-default-blue">{{ more.icon }}</v-icon>
                </v-list-item-icon>
                <v-list-item-title class="font-weight-500 font-size-14">
                  {{ more.title }}
                </v-list-item-title>
              </v-list-item>
            </template>
          </v-list>
        </v-menu>

        <v-btn
          depressed
          class="mx-2 custom-grey-border custom-bold-button"
          v-on:click="goBack"
        >
          <v-icon small left>mdi-keyboard-backspace</v-icon>
          Back
        </v-btn>
      </div>
    </template>

    <!-- Main Content Area -->
    <template v-slot:body>
      <template v-if="pageLoading">
        <v-container fluid class="px-0">
          <v-skeleton-loader
            class="custom-skeleton mb-4"
            type="table-tbody"
          ></v-skeleton-loader>
        </v-container>
      </template>

      <v-container class="px-0" fluid v-else>
        <!-- Tabs Navigation -->
        <v-tabs
          active-class="custom-tab-active"
          v-model="paymentTab"
          background-color="transparent"
          color="cyan"
          class="custom-tab-transparent tab-sticky"
        >
          <v-tab class="font-size-16 font-weight-600 px-8" href="#overview">
            <span class="svg-icon svg-icon-lg mr-4 svg-v-list-icon">
              <inline-svg :src="$assetURL('media/custom-svg/property.svg')" />
            </span>
            Overview
          </v-tab>
          <v-tab
            class="font-size-16 font-weight-600 px-8"
            href="#notes-attachment"
          >
            <span class="svg-icon svg-icon-lg mr-4 svg-v-list-icon">
              <inline-svg :src="$assetURL('media/custom-svg/attachment.svg')" />
            </span>
            Internal Notes & Attachments
          </v-tab>
          <v-tab
            v-if="getPermission('history:view') && false"
            class="font-size-16 font-weight-600 px-8"
            href="#history"
          >
            <span class="svg-icon svg-icon-lg mr-4 svg-v-list-icon">
              <inline-svg :src="$assetURL('media/custom-svg/history.svg')" />
            </span>
            History
          </v-tab>
        </v-tabs>

        <perfect-scrollbar
          :options="{ suppressScrollX: true }"
          class="scroll"
          style="max-height: calc(100vh - 200px); position: relative"
        >
          <v-tabs-items v-model="paymentTab">
            <!-- Overview Tab -->
            <v-tab-item value="overview">
              <v-container fluid class="px-0 pt-4">
                <!-- Location & Customer Card -->
                <v-card outlined elevation="0" class="mb-4">
                  <div
                    class="grey lighten-5 d-flex align-center justify-space-between px-4 py-3 border-bottom"
                  >
                    <div class="d-flex align-center">
                      <v-icon color="blue darken-2" small class="mr-2"
                        >mdi-map-marker-outline</v-icon
                      >
                      <h3
                        class="font-weight-700 color-custom-blue font-size-18 mb-0 text-uppercase"
                      >
                        Customer & Location Details
                      </h3>
                    </div>
                  </div>
                  <v-card-text class="pa-4 font-size-16">
                    <span
                      class="color-custom-blue font-weight-700 font-size-18 d-block mb-3"
                      v-if="customer && customer.id"
                    >
                      <router-link
                        :to="
                          getDefaultRoute('customer.detail', {
                            params: { id: customer.id },
                          })
                        "
                        class="color-custom-blue font-size-16 text-decoration-none d-inline-flex align-center"
                      >
                        <v-icon small color="blue darken-2" class="mr-1"
                          >mdi-account</v-icon
                        >
                        {{ customer.display_name }}
                        <v-icon small color="blue darken-2" class="ml-1"
                          >mdi-open-in-new</v-icon
                        >
                      </router-link>
                    </span>

                    <v-layout class="my-2 flex-wrap">
                      <!-- Service Location / Property Address -->
                      <v-flex
                        md6
                        class="custom-border-right pr-4 mb-3"
                        v-if="property"
                      >
                        <table width="100%">
                          <tr>
                            <td
                              class="font-weight-700 color-custom-blue pb-2 font-size-18 text-uppercase"
                            >
                              <v-icon small color="cyan darken-2" class="mr-1"
                                >mdi-map-marker-outline</v-icon
                              >
                              Service Location
                            </td>
                          </tr>
                          <tr>
                            <td class="py-0 font-size-18 font-weight-500">
                              <a
                                v-if="property.property_address"
                                target="_blank"
                                :href="`https://maps.google.com/?q=${property.property_address}`"
                                class="cyan--text text--darken-3 text-decoration-none d-inline-flex align-items-start"
                              >
                                <v-icon
                                  small
                                  color="cyan darken-3"
                                  class="mr-1 mt-0"
                                  >mdi-map-marker</v-icon
                                >
                                <span>{{ property.property_address }}</span>
                              </a>
                              <span v-else-if="PropertyDetail">{{
                                PropertyDetail
                              }}</span>
                              <em v-else class="text-muted"
                                >No address specified</em
                              >
                            </td>
                          </tr>
                          <tr>
                            <td
                              class="font-weight-700 color-custom-blue pt-3 pb-1 font-size-18 text-uppercase"
                            >
                              <v-icon small color="blue darken-4" class="mr-1"
                                >mdi-card-account-phone-outline</v-icon
                              >
                              Contact details
                            </td>
                          </tr>
                          <tr
                            v-if="
                              property_contact_person &&
                              property_contact_person.display_name
                            "
                          >
                            <td class="py-0 font-weight-500">
                              <v-icon small color="blue darken-4" class="mr-1"
                                >mdi-account-outline</v-icon
                              >
                              {{ property_contact_person.display_name }}
                            </td>
                          </tr>
                          <tr
                            v-if="
                              property_contact_person &&
                              property_contact_person.primary_phone
                            "
                          >
                            <td class="py-0 font-weight-500">
                              <a
                                :href="
                                  'tel:' + property_contact_person.primary_phone
                                "
                                class="text-decoration-none"
                              >
                                <v-icon small color="blue darken-4" class="mr-1"
                                  >mdi-phone</v-icon
                                >
                                {{ property_contact_person.primary_phone }}
                              </a>
                            </td>
                          </tr>
                          <tr
                            v-if="
                              property_contact_person &&
                              property_contact_person.primary_email
                            "
                          >
                            <td class="py-0 font-weight-500">
                              <a
                                :href="
                                  'mailto:' +
                                  property_contact_person.primary_email
                                "
                                class="text-decoration-none"
                              >
                                <v-icon small color="blue darken-4" class="mr-1"
                                  >mdi-email</v-icon
                                >
                                {{ property_contact_person.primary_email }}
                              </a>
                            </td>
                          </tr>
                        </table>
                      </v-flex>

                      <!-- Billing Address -->
                      <v-flex md6 class="pl-4 mb-3" v-if="billing">
                        <table width="100%">
                          <tr>
                            <td
                              class="font-weight-700 color-custom-blue pb-2 font-size-18 text-uppercase"
                            >
                              <v-icon small color="blue darken-2" class="mr-1"
                                >mdi-map-marker-radius-outline</v-icon
                              >
                              Billing Address
                            </td>
                          </tr>
                          <tr>
                            <td class="py-0 font-size-18 font-weight-500">
                              <a
                                v-if="billing.property_address"
                                target="_blank"
                                :href="`https://maps.google.com/?q=${billing.property_address}`"
                                class="cyan--text text--darken-3 text-decoration-none d-inline-flex align-items-start"
                              >
                                <v-icon
                                  small
                                  color="cyan darken-3"
                                  class="mr-1 mt-0"
                                  >mdi-map-marker</v-icon
                                >
                                <span>{{ billing.property_address }}</span>
                              </a>
                              <span v-else-if="BillingDetail">{{
                                BillingDetail
                              }}</span>
                              <em v-else class="text-muted"
                                >No billing address specified</em
                              >
                            </td>
                          </tr>
                          <tr>
                            <td
                              class="font-weight-700 color-custom-blue pt-3 pb-1 font-size-18 text-uppercase"
                            >
                              <v-icon small color="blue darken-4" class="mr-1"
                                >mdi-card-account-phone-outline</v-icon
                              >
                              Contact details
                            </td>
                          </tr>
                          <tr
                            v-if="
                              billing_contact_person &&
                              billing_contact_person.display_name
                            "
                          >
                            <td class="py-0 font-weight-500">
                              <v-icon small color="blue darken-4" class="mr-1"
                                >mdi-account-outline</v-icon
                              >
                              {{ billing_contact_person.display_name }}
                            </td>
                          </tr>
                          <tr
                            v-if="
                              billing_contact_person &&
                              billing_contact_person.primary_phone
                            "
                          >
                            <td class="py-0 font-weight-500">
                              <a
                                :href="
                                  'tel:' + billing_contact_person.primary_phone
                                "
                                class="text-decoration-none"
                              >
                                <v-icon small color="blue darken-4" class="mr-1"
                                  >mdi-phone</v-icon
                                >
                                {{ billing_contact_person.primary_phone }}
                              </a>
                            </td>
                          </tr>
                          <tr
                            v-if="
                              billing_contact_person &&
                              billing_contact_person.primary_email
                            "
                          >
                            <td class="py-0 font-weight-500">
                              <a
                                :href="
                                  'mailto:' +
                                  billing_contact_person.primary_email
                                "
                                class="text-decoration-none"
                              >
                                <v-icon small color="blue darken-4" class="mr-1"
                                  >mdi-email</v-icon
                                >
                                {{ billing_contact_person.primary_email }}
                              </a>
                            </td>
                          </tr>
                        </table>
                      </v-flex>
                    </v-layout>
                  </v-card-text>
                </v-card>

                <!-- Invoice Information Card (When Invoice exists) -->
                <v-card
                  outlined
                  elevation="0"
                  class="mb-4"
                  v-if="detail.invoice && detail.invoice.id"
                >
                  <div
                    class="grey lighten-5 d-flex align-center justify-space-between px-4 py-3 border-bottom"
                  >
                    <div class="d-flex align-center">
                      <v-icon color="blue darken-2" small class="mr-2"
                        >mdi-file-document-outline</v-icon
                      >
                      <h3
                        class="font-weight-700 color-custom-blue font-size-18 mb-0 text-uppercase"
                      >
                        Invoice Information
                      </h3>
                    </div>
                  </div>
                  <v-card-text class="pa-4 font-size-16">
                    <v-row>
                      <v-col md="4" cols="12" class="pt-0">
                        <h4
                          class="color-custom-blue text-uppercase font-size-16 font-weight-600 mb-2"
                        >
                          Invoice #
                        </h4>
                        <div class="custom-grey-border p-3 rounded">
                          <router-link
                            :to="
                              getDefaultRoute('invoice.detail', {
                                params: { id: detail.invoice.id },
                              })
                            "
                            class="font-weight-600 font-size-16"
                          >
                            {{ detail.invoice.barcode }}
                          </router-link>
                        </div>
                      </v-col>
                      <v-col md="4" cols="12" class="pt-0">
                        <h4
                          class="color-custom-blue text-uppercase font-size-16 font-weight-600 mb-2"
                        >
                          Invoice Date
                        </h4>
                        <div
                          class="custom-grey-border p-3 rounded font-size-16 font-weight-500"
                        >
                          {{
                            detail.invoice.invoice_date
                              ? formatDate(detail.invoice.invoice_date)
                              : "-"
                          }}
                        </div>
                      </v-col>
                      <v-col md="4" cols="12" class="pt-0">
                        <h4
                          class="color-custom-blue text-uppercase font-size-16 font-weight-600 mb-2"
                        >
                          Invoice Amount
                        </h4>
                        <div
                          class="custom-grey-border p-3 rounded font-size-16 font-weight-600 color-custom-blue"
                        >
                          {{
                            detail.invoice.total
                              ? formatMoney(detail.invoice.total)
                              : "-"
                          }}
                        </div>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-container>
            </v-tab-item>

            <!-- Notes & Attachments Tab -->
            <v-tab-item value="notes-attachment">
              <v-container fluid class="px-0 pt-4">
                <InternalNoteAttachmentDetail
                  isPayment
                  type="payment"
                  :detail="notesAttachmentData"
                  :canUpdate="canUpdatePayment"
                ></InternalNoteAttachmentDetail>
              </v-container>
            </v-tab-item>

            <!-- History Tab -->
            <v-tab-item v-if="getPermission('history:view')" value="history">
              <v-container fluid class="px-0 pt-0">
                <InternalHistoryDetail
                  type="payment"
                  :type_id="detail && detail.id ? detail.id : 0"
                ></InternalHistoryDetail>
              </v-container>
            </v-tab-item>
          </v-tabs-items>
        </perfect-scrollbar>
      </v-container>
    </template>
    <template v-slot:footer> </template>
  </DetailTemplate>
</template>

<script>
import DetailPaymentMixin from "@/core/lib/payment/detail.payment.mixin";
import DetailTemplate from "@/view/pages/partials/Detail-Template.vue";
import CustomStatus from "@/view/pages/partials/CustomStatus.vue";
import InternalNoteAttachmentDetail from "@/view/pages/partials/Detail/Internal-Note-Attachment-Detail.vue";
import UpdateTooltip from "@/view/pages/partials/UpdateTooltip.vue";
import InternalHistoryDetail from "@/view/pages/partials/Detail/Internal-History-Detail.vue";

export default {
  mixins: [DetailPaymentMixin],
  data() {
    return {
      paymentTab: "overview",
    };
  },
  components: {
    UpdateTooltip,
    DetailTemplate,
    CustomStatus,
    InternalNoteAttachmentDetail,
    InternalHistoryDetail,
  },
};
</script>
